import React from 'react'
import { observer } from 'mobx-react'
import { toJS } from 'mobx'

import CoursePreview from 'shared/screens/course-preview/CoursePreview'

import { composeProps } from 'shop/utils/compose-props.utils'
import { withCommonStores } from 'shop/utils/with-common-stores.utils'
import { withLocale } from 'shop/utils/with-locale.utils'
import { withRootStore } from 'shop/utils/with-root-store.utils'
import { withScreenSize } from 'shop/utils/with-screen-size.utils'
import { withCacheControl } from 'shop/utils/cache-control.utils'
import { withGSSPLogger } from 'shop/utils/logger.utils'
import { fetchContentPageData } from 'shop/utils/fetch-content-page.utils'
import { useNextRouter } from 'shop/hooks/use-next-js-router'
import { withUserSessionId } from 'shop/utils/with-user-session-id.utils'
import { withEnvVars } from 'shop/utils/withEnvVars'
import { withApiClient } from 'shop/utils/with-api-client.utils'
import { withLogger } from 'shop/utils/with-logger.utils'
import { withShopThemeStore } from 'shop/utils/with-shop-theme-store.utils'
import { withExperiments } from 'shop/utils/with-experiments.utils'
import { useShopStores } from 'shop/hooks/use-store'
import { withTranslations } from 'shop/utils/with-translations.utils'

interface CoursePreviewPageParams {
  productId: string
  username: string
}

interface CoursePreviewPageProps {
  screenSize: string
  contentPageId: string
  contentPageData: any // TODO: SSR - rewrite fetchContentPage and screen_size in isomorphic way with MOBX store without Component state
}

const CoursePreviewPage: React.FC<CoursePreviewPageProps> = observer(
  ({ contentPageData, contentPageId, screenSize }) => {
    const {
      courseSessionsStore,
      courseViewsStore,
      productSessionStore,
      contentPageStore,
      membershipThemesStore,
      sellerStore,
      sellersStore,
      commentsStore,
      sellerFontsStore,
      payerStore,
      sellablesStore,
      ordersStore,
      currenciesStore,
      quizzesStore,
      lessonsStore,
      communitiesStore,
      communityMemberStore,
    } = useShopStores()
    const { historyPush, historyReplace, pathname, params, historyGoBack, LinkComponent } =
      useNextRouter<CoursePreviewPageParams>()
    return (
      <CoursePreview
        preloadedContentPage={{
          contentPageData,
          contentPageId,
          screenSize,
        }}
        historyPush={historyPush}
        historyReplace={historyReplace}
        historyGoBack={historyGoBack}
        productSlug={params.productId}
        username={params.username}
        pathname={pathname}
        urlParams={params}
        queryParams={params}
        LinkComponent={LinkComponent}
        courseSessionsStore={courseSessionsStore}
        courseViewsStore={courseViewsStore}
        productSessionStore={productSessionStore}
        contentPageStore={contentPageStore}
        membershipThemesStore={membershipThemesStore}
        sellerStore={sellerStore}
        sellersStore={sellersStore}
        commentsStore={commentsStore}
        sellerFontsStore={sellerFontsStore}
        payerStore={payerStore}
        sellablesStore={sellablesStore}
        ordersStore={ordersStore}
        currenciesStore={currenciesStore}
        quizzesStore={quizzesStore}
        lessonsStore={lessonsStore}
        communitiesStore={communitiesStore}
        communityMemberStore={communityMemberStore}
      />
    )
  }
)

export default CoursePreviewPage

const getPageProps = async (context) => {
  const screenSize = context.screenSize
  const { productId, username, lesson_id } = context.query

  await context.rootStore.productsStore.fetchProduct(productId, '', true)
  context.rootStore.courseViewsStore.setSideMenuState(false)

  await context.rootStore.courseViewsStore.prepareCourse({
    productId,
    lessonId: lesson_id,
    username,
    historyPush: () => {},
    historyReplace: () => {},
  })

  const props = {
    isProductPage: true,
    initialData: {
      productsStore: {
        product: toJS(context.rootStore.productsStore.product),
      },
      courseViewsStore: {
        product: toJS(context.rootStore.courseViewsStore.product),
        courseThemeData: toJS(context.rootStore.courseViewsStore.courseThemeData),
        lessons: toJS(context.rootStore.courseViewsStore.lessons),
        loading: toJS(context.rootStore.courseViewsStore.loading),
        inited: toJS(context.rootStore.courseViewsStore.inited),
        mobileMenuActive: toJS(context.rootStore.courseViewsStore.mobileMenuActive),
        activeLesson: toJS(context.rootStore.courseViewsStore.activeLesson),
        sharingToken: toJS(context.rootStore.courseViewsStore.sharingToken),
      },
    },
    screenSize,
    contentPageId: null,
    contentPageData: null,
  }

  const includesContentPage = ['contentPageAfterCont', 'contentPageCont', 'contentPageBeforeCont'].includes(
    context.rootStore.courseViewsStore.viewConfig.contentToDisplayKey
  )

  const activeLessonContentIds = {
    contentPageAfterCont: context.rootStore.courseViewsStore.activeLesson.contentPageAfterId,
    contentPageBeforeCont: context.rootStore.courseViewsStore.activeLesson.contentPageBeforeId,
    contentPageCont: context.rootStore.courseViewsStore.activeLesson.contentPageId,
  }

  const contentPageId = activeLessonContentIds[context.rootStore.courseViewsStore.viewConfig.contentToDisplayKey]

  if (includesContentPage && contentPageId) {
    const {
      contentPageData: fetchedContentPageData,
      contentPageStore,
      affiliateProgramsStore,
      notFound,
    } = await fetchContentPageData(
      {
        username,
        contentPageId,
        screenSize,
      },
      context
    )

    if (notFound) {
      return {
        notFound: true,
        props: {},
      }
    }

    props.contentPageId = contentPageId
    props.contentPageData = fetchedContentPageData
    // @ts-ignore
    props.initialData.contentPageStore = contentPageStore
    // @ts-ignore
    props.initialData.affiliateProgramsStore = affiliateProgramsStore
    // @ts-ignore
    props.initialData.productGroupsStore = {
      list: toJS(context.rootStore.productGroupsStore.list),
      loading: toJS(context.rootStore.productGroupsStore.loading),
    }
  }

  return {
    props,
  }
}

export const getServerSideProps = withGSSPLogger(
  composeProps(
    withLocale,
    withTranslations,
    withApiClient,
    withEnvVars,
    withUserSessionId,
    withRootStore,
    withScreenSize,
    withCommonStores,
    withShopThemeStore,
    getPageProps,
    withExperiments,
    withCacheControl(60),
    withLogger
  ),
  'getServerSideProps CoursePreviewPage'
)
